import React, { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { isNativeApp } from "../../platform/native";
import { backgroundHrDiagnostics, clearBackgroundHrDiagnostics } from "../../platform/backgroundHr";

/**
 * Android app only: what the background services did (DiagLog.java), so a
 * screen-off test can be read back and shared from the phone.
 */
const BackgroundLogCard = () => {
  const [log, setLog] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setLog(await backgroundHrDiagnostics());
    } catch (err) {
      setLog({ lines: [`Couldn't read the log: ${err?.message || err}`] });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!isNativeApp()) return null;
  const lines = log?.lines || [];
  const state = log
    ? `Heart rate service ${log.running ? "running" : "stopped"}${log.running ? `, strap link ${log.linkUp ? "up" : "down"}` : ""}`
    : "Loading…";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText([state, ...lines].join("\n"));
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <CardTitle>Background log</CardTitle>
        <CardDescription>
          What the app did while it was in the background or the screen was off. Copy it into a message after a
          test.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm">{state}</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={load}>
            Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={copy} disabled={!lines.length}>
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={async () => {
              await clearBackgroundHrDiagnostics().catch(() => {});
              setCopied(false);
              load();
            }}
            disabled={!lines.length}
          >
            Clear
          </Button>
        </div>
        <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md bg-muted/40 p-2 font-mono text-[11px] leading-snug text-muted-foreground">
          {lines.length ? lines.join("\n") : "Nothing logged yet. Start a workout with your strap connected."}
        </pre>
      </CardContent>
    </Card>
  );
};

export default BackgroundLogCard;
